import { dot, makeAabb, makeObb, obbRadiusOn, satAxes } from '../engine/obb'
import { Quat, Vec3 } from '../types'

/**
 * プログラム移動する部品がアバターへ食い込んだときの「押し出し量」を、
 * 部品の実際の姿勢（OBB = 回転した直方体）で解く幾何ヘルパ。
 *
 * 以前はワールド AABB（rotatedAabbHalfExtents）で重なりと押し出し先を
 * 決めていた。斜めに回したブロックでは AABB が実体よりはるかに大きく、
 *  - 見た目には触れていない角の空白でも押される（見えない当たり判定）
 *  - 押し出し先が AABB の面なので一気に数 studs 飛ぶ（ワープ）
 * が起きていた（RJF10 の「回しながら動かす」プログラムで顕著）。
 *
 * 判定は engine/obb の SAT（OBB × アバターのカプセル外接箱）で行い、
 *  1. そもそも重なっているか（15 軸のいずれかで分離していれば重なっていない）
 *  2. 重なっているなら、指定方向へ何 studs 動かせば分離するか（各軸の
 *     分離必要量の最小値）
 * を厳密に求める。軸平行な部品では結果が従来式（面 + アバター半径 + 余白）と
 * 一致するため、回っていないブロックの挙動は変わらない。
 */

/** 押し出し方向成分がこの値以下の軸は、その方向へ動いても分離できない。 */
const MIN_DIRECTION_COMPONENT = 1e-6

export interface ProgramPushQuery {
  /** 部品（回転した直方体）。half は size/2。 */
  box: { center: Vec3; rotation: Quat; half: Vec3 }
  /** アバター（カプセルの外接箱）。half は (半径, 半高 + 半径, 半径)。 */
  avatar: { center: Vec3; half: Vec3 }
  /** 押し出す向き（±X / ±Z の単位ベクトル）。 */
  direction: Vec3
  /** 分離後に空ける余白（studs）。0 だと次フレームで再び食い込む。 */
  margin: number
}

/**
 * 部品とアバターが実際に重なっているとき、`direction` へ何 studs 動かせば
 * 分離するか（余白込み）を返す。重なっていなければ null。
 *
 * SAT の候補軸それぞれについて「その軸で分離するのに必要な移動量」を求め、
 * 最小値を返す。1 軸でも分離すれば箱同士は離れるため、最小値が必要十分な
 * 押し出し量になる。
 */
export const programPushDistance = ({
  box,
  avatar,
  direction,
  margin,
}: ProgramPushQuery): number | null => {
  const partObb = makeObb(box.center, box.rotation, box.half)
  const avatarObb = makeAabb(avatar.center, avatar.half)
  const center: Vec3 = {
    x: box.center.x - avatar.center.x,
    y: box.center.y - avatar.center.y,
    z: box.center.z - avatar.center.z,
  }
  let best = Infinity
  for (const n of satAxes(avatarObb, partObb)) {
    // 中心間距離の n 成分と、両者の投影半径の和（= 接するときの距離）。
    const separation = dot(center, n)
    const contact = obbRadiusOn(avatarObb, n) + obbRadiusOn(partObb, n)
    if (Math.abs(separation) >= contact) {
      // この軸で既に分離している＝重なっていない。押し出す必要はない。
      return null
    }
    const along = dot(n, direction)
    if (Math.abs(along) < MIN_DIRECTION_COMPONENT) {
      // direction へ動かしても、この軸上の重なりは変わらない。
      continue
    }
    // アバターを t 動かすと separation は t*along だけ減る。余白のぶん
    // 踏み越した位置（接触距離 + margin）まで動かす量が、この軸の必要量。
    const required = contact + margin
    const distance =
      along > 0 ?
        (separation + required) / along
      : (separation - required) / along
    if (distance < best) {
      best = distance
    }
  }
  // どの軸でも分離できない（有限の箱では起こらないが、数値的な保険）。
  return Number.isFinite(best) ? Math.max(0, best) : null
}
